import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  ExpiredAltIdMatchViolationsQuery,
  HarmonizedViolationResolutionStatus,
  HarmonizedViolationSeverity,
} from "generated-graphql/graphql";
import { EXPIRED_ALT_ID_MATCH_VIOLATIONS } from "hooks/useExpiredAltIdMatchReviews";
import { Link as RouterLink } from "react-router-dom";

import { expiryDisplay, formatDate, matchKindLabel, ReviewRow } from "./reviewDisplay";

type HeldViolation = ExpiredAltIdMatchViolationsQuery["expiredAltIdMatchViolations"][number];

/**
 * Severity drives the decision: releasing a group wrongly matters most when it
 * carries HIGH rows, and unresolved rows are the ones pushed to Amazon's
 * Airtable with no way back.
 */
function severityColor(severity: HarmonizedViolationSeverity) {
  if (severity === HarmonizedViolationSeverity.High) {
    return "error" as const;
  }
  if (severity === HarmonizedViolationSeverity.Medium) {
    return "warning" as const;
  }
  return "default" as const;
}

function LabelledValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

function HeldViolationRow({ violation }: { violation: HeldViolation }) {
  const dates = [violation.violationStartDate, violation.violationEndDate]
    .filter((value): value is string => Boolean(value))
    .map((value) => formatDate(value));
  const dateLabel = dates.length === 2 && dates[0] !== dates[1] ? dates.join(" – ") : dates[0];

  return (
    <Stack spacing={0.5} sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight="bold">
          {violation.violationId}
        </Typography>
        <StatusChip
          color={severityColor(violation.standardizedSeverity)}
          label={prettyPrintEnumValue(violation.standardizedSeverity)}
        />
        <StatusChip color="default" label={prettyPrintEnumValue(violation.sourceProgram)} />
        {violation.standardizedResolutionStatus ===
        HarmonizedViolationResolutionStatus.Unresolved ? (
          <StatusChip color="warning" variant="outlined" label="Unresolved" />
        ) : null}
      </Stack>
      {violation.violationDescription ? (
        <Typography variant="body2" color="text.secondary">
          {violation.violationDescription}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary">
        {dateLabel ?? "No violation date"} · {violation.facilityName}
      </Typography>
    </Stack>
  );
}

/** The group's identity and why it is held — everything above the violations. */
function MatchSummary({ row }: { row: ReviewRow }) {
  return (
    <>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <LabelledValue label="Facility">
          <Typography
            component={RouterLink}
            to={`/o/${row.tenantId}/facilities/${row.facilityId}/profile`}
            target="_blank"
            rel="noopener"
            variant="body2"
          >
            {row.facilityName}
          </Typography>
        </LabelledValue>
        <LabelledValue label="Customer">
          <Typography variant="body2">{row.tenantName}</Typography>
        </LabelledValue>
        <LabelledValue label="Match">
          <Typography variant="body2">{matchKindLabel(row.matchKind)}</Typography>
        </LabelledValue>
        <LabelledValue label="Expired">
          {row.alternateIds.length === 0 ? (
            <StatusChip color="default" label="Orphaned — ID deleted" />
          ) : (
            <Typography variant="body2">{expiryDisplay(row.alternateIds)}</Typography>
          )}
        </LabelledValue>
      </Stack>
      {row.activeElsewhere ? (
        <StatusChip
          color="warning"
          label="This ID is still live at another site — it may have been transferred"
        />
      ) : null}
    </>
  );
}

/**
 * The held violations behind one match group, so the accept/fix decision is made
 * against what is actually being released rather than against a count.
 *
 * Unresolved rows are called out because those are the ones the Airtable sync
 * picks up, and that sync is insert-only — accepting a group with unresolved
 * rows is the part of this decision that cannot be undone.
 */
export function ViolationMatchDrawer({
  row,
  onClose,
  onDecide,
  saving,
}: {
  row: ReviewRow | null;
  onClose: () => void;
  onDecide: (row: ReviewRow, accepted: boolean) => void;
  saving: boolean;
}) {
  const { data, loading } = useQuery(EXPIRED_ALT_ID_MATCH_VIOLATIONS, {
    variables: { id: row?.id ?? "" },
    skip: !row,
  });

  if (!row) {
    // A closed RightDrawerShell still leaves its paper in the DOM, because the
    // shell sets ModalProps={{ keepMounted: true }}.
    return null;
  }

  const violations = data?.expiredAltIdMatchViolations ?? [];
  const unresolved = violations.filter(
    (violation) =>
      violation.standardizedResolutionStatus === HarmonizedViolationResolutionStatus.Unresolved
  ).length;
  const accepted = Boolean(row.acceptedAt);

  return (
    <RightDrawerShell
      open
      onClose={onClose}
      title={<Typography variant="h6">{row.altIdValue}</Typography>}
    >
      <Box role="region" aria-label="Expired ID match review">
        <Stack spacing={2}>
          <MatchSummary row={row} />

          <Divider />

          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="subtitle2">
              {row.violationCount} held violation{row.violationCount === 1 ? "" : "s"}
            </Typography>
            {unresolved > 0 ? (
              <Typography variant="caption" color="text.secondary">
                {unresolved} unresolved — accepting sends {unresolved === 1 ? "it" : "them"} to the
                customer&apos;s Airtable, which cannot be retracted
              </Typography>
            ) : null}
          </Stack>

          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <Stack divider={<Divider flexItem />}>
              {violations.map((violation) => (
                <HeldViolationRow key={violation.id} violation={violation} />
              ))}
            </Stack>
          )}
          {!loading && violations.length < row.violationCount ? (
            <Typography variant="caption" color="text.secondary">
              Showing the {violations.length} highest-severity of {row.violationCount}.
            </Typography>
          ) : null}

          <Divider />

          <Stack direction="row" spacing={1}>
            <Button
              variant={accepted ? "text" : "contained"}
              color="secondary"
              disabled={saving}
              onClick={() => onDecide(row, !accepted)}
            >
              {accepted ? "Un-accept" : "Accept match"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </RightDrawerShell>
  );
}
